import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {DetectMethodChanges, DetectSetterChanges, getStrikeStep} from '../../utils';
import {snapToStrikeStep} from '../../copilot-shared/opg-copilot-questionnaire';
import {OptionExpirationDescriptor} from '../../shell-communication/shell-dto-protocol';
import {TradingInstrument} from '../../trading-instruments/trading-instrument.class';
import {OPG_RELATIVE_RISK_LEVELS} from '../../copilot-shared/opg-copilot-domain';
import {convertOnNormalizedModeFlip} from '../../copilot-shared/normalized-raw-conversion';
import {mavenRangeMode, MavenCoverage, MavenCoverageKind, MavenMaxRisk, MavenRangeMode, MavenRiskLevel, MavenSideInputs} from '../maven-domain';
import {MavenChip, sideChips} from './maven-setup-breadcrumbs';
import {anyKeyWithPrefix, MavenParamChangeMap} from './maven-setup-param-diff';

// ONE SIDE'S HEDGE PARAMETERS (CR-20260910-001 milestone 4, S5-S9). Instantiated TWICE — calls
// and puts — and that is the only reason it is a component rather than markup in the Setup.
//
// ⚠ WHY THIS IS NOT THE ORION (OPG) SIDE QUESTIONNAIRE, which the work breakdown said to reuse.
// The two ask different questions. The OPG's price range has FOUR modes and its bounded one wants
// a START STRIKE AND AN END STRIKE (opg-copilot-questionnaire.ts validates both, and their
// direction). Maven's coverage has THREE kinds, its Range wants an END STRIKE OR DISTANCE — the start is
// the paired adjustment solution's short strike plus this side's offset, which the trader never
// enters and the panel never knows before the run — and Maven adds a question the OPG does not
// have at all: the offset from shorts (S7). "Both" is not offered here either (ruling Q-M2),
// while the OPG's atmOffset mode offers exactly that.
//
// So the CONTROLS are reused and the COMPONENT is not: the four risk levels come from
// OPG_RELATIVE_RISK_LEVELS and the normalized/raw flip from convertOnNormalizedModeFlip, both
// imported as pure values with no module change and no possibility of drift. What is Maven's own
// is Maven's own, written once, here.
//
// CONTRACT WITH THE PARENT: `inputs` is a WORKING object OWNED BY THE PARENT; edits mutate it in
// place and `changed` fires after every user-driven mutation. A mode switch REPLACES the variant
// object (the unions are discriminated), never patches it. Setters hydrate and emit NOTHING —
// only a user action emits, or the parent's own write would echo back at it forever.
@Component({
    selector: 'ets-maven-side-parameters',
    templateUrl: './maven-side-parameters.component.html',
    styleUrls: ['./maven-side-parameters.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MavenSideParametersComponent {

    constructor(private readonly _changeDetector: ChangeDetectorRef) {
    }

    // 'calls' | 'puts'. Drives the heading and, more importantly, the DIRECTION language: calls
    // cover upward from the anchor and puts downward.
    @Input() side: 'calls' | 'puts' = 'calls';
    @Input() presetApgSource: string = null;

    private _inputs: MavenSideInputs;
    private _parkedDollarRisk: Extract<MavenMaxRisk, {kind: 'dollar'}> | null = null;
    private _parkedRelativeRisk: Extract<MavenMaxRisk, {kind: 'relative'}> | null = null;
    private _parkedRangeCoverage: MavenCoverage | null = null;

    @Input()
    @DetectSetterChanges()
    set inputs(value: MavenSideInputs) {
        // Reset, portfolio changes and template restoration supply a new working side.
        if (value !== this._inputs) {
            this._parkedDollarRisk = null;
            this._parkedRelativeRisk = null;
            this._parkedRangeCoverage = null;
        }
        this._inputs = value;
        // A side hydrated as enabled opens; one hydrated closed stays closed. The parent's Reset
        // hands in a fresh, disabled object, which is what folds both sides up again.
        this.expanded = !!value && !!value.enabled;
    }

    get inputs(): MavenSideInputs {
        return this._inputs;
    }

    // The portfolio's default order quantity, for the normalized <-> raw conversions. null while
    // no portfolio is chosen: the flip then leaves the typed number alone rather than guessing a
    // factor, which is the shared helper's own documented behaviour.
    @Input() defaultQty: number | null = null;

    // Fed to the strike picker. The platform's ets-strikes-dropdown, never a plain number box.
    @Input() targetInstrument: TradingInstrument | null = null;
    @Input() strikeExpiration: OptionExpirationDescriptor | null = null;

    // ⚠ Expected Range is DISABLED IN PLACE, never hidden, when the trader's Expected Move
    // setting cannot produce a range — the ORION (OPG)'s own rule and the owner's explicit ruling
    // there: a pill that vanishes reflows the group and hides the fact that the mode exists at
    // all; a greyed one says "not now" instead of "never was".
    @Input() expectedRangeAvailable = true;

    // Locked while a run is in flight: the sent Setup is a snapshot, so mid-flight edits must be
    // impossible rather than merely unrecorded.
    @Input() disabled = false;

    // The problems this side currently has, already filtered to this control by the parent.
    // Rendered in place, under the block.
    @Input() problems: string[] = [];

    @Output() changed = new EventEmitter<void>();

    // ---- The changed-since-run cues (owner vrequest 2026-09-15) ------------------------------
    //
    // The Setup's diff of the live Setup against the last run's snapshot, in the grammar of
    // maven-setup-param-diff.ts. This component reads ITS side's subtree ('calls.' / 'puts.') and
    // paints: the changed control, the folded cradle's chip, and the side header when anything
    // under it changed. A FRESH object per recompute is what wakes this OnPush child. Null =
    // clean, every cue off.
    @Input() changedKeys: MavenParamChangeMap | null = null;

    private get keyPrefix(): string {
        return this.side + '.';
    }

    // Template guard for one control's [class.param-changed]; `suffix` is the key below the side.
    isChanged(suffix: string): boolean {
        return !!(this.changedKeys && this.changedKeys[this.keyPrefix + suffix]);
    }

    // The side header's cue: anything on this side changed.
    get anyChanged(): boolean {
        return anyKeyWithPrefix(this.changedKeys, [this.keyPrefix]);
    }

    // A cradle chip's cue: the chip's key is relative to the side.
    chipChanged(c: MavenChip): boolean {
        return !!c.key && anyKeyWithPrefix(this.changedKeys, [this.keyPrefix + c.key]);
    }

    get title(): string {
        return this.side === 'calls' ? 'Calls' : 'Puts';
    }

    get enabled(): boolean {
        return !!this._inputs && this._inputs.enabled;
    }

    // ---- The collapsible (vrequest 2026-09-13, item 6) --------------------------------------
    //
    // `expanded` is PRESENTATION ONLY and is never sent: whether the side is USED is `enabled`
    // on the inputs. Checking the box opens the body and unchecking closes it; the chevron moves
    // the body alone, so a trader can fold an enabled side away and it still runs.
    expanded = false;

    @DetectMethodChanges()
    toggleExpanded(): void {
        this.expanded = !this.expanded;
    }

    // The parent's Expand all / Collapse all (vrequest 2026-09-15). Moves the body only, exactly
    // as the chevron does — the checkbox is never touched. This child is OnPush, so the parent's
    // own detection would not repaint it: the decorator runs this component's. The parent never
    // reads this flag back: the sides are "weak" for its label (owner, second pass the same day).
    @DetectMethodChanges()
    setExpanded(value: boolean): void {
        this.expanded = value;
    }

    // The folded side's breadcrumb cradle (owner vrequest 2026-09-15): what this side holds, as
    // chips, under its header while the body is folded. An unchecked side reads "Not Set". The
    // formatter owns the wording (maven-setup-breadcrumbs.ts).
    get crumbs(): MavenChip[] {
        return sideChips(this._inputs);
    }

    // ---- Required markers (vrequest 2026-09-13, item 3) --------------------------------------
    //
    // ONE definition of "missing" per field, the SAME tests maven-setup-validation.ts applies in
    // sideProblems(): the star beside a label and the refusal at Apply must agree, or the trader
    // sees a starred field on a side the validator accepts, or the reverse.

    get offsetMissing(): boolean {
        return !this._inputs || !isFiniteNumber(this._inputs.offsetFromShorts);
    }

    get coverageMissing(): boolean {
        return !this._inputs || !this._inputs.coverage;
    }

    get endStrikeMissing(): boolean {
        return this.isRangeCoverage && !this.isDistanceRange && !isFiniteNumber(this._inputs.coverage.endStrike);
    }

    get rangeSizeMissing(): boolean {
        return this.isRangeCoverage && this.isDistanceRange
            && (!isFiniteNumber(this._inputs.coverage.rangeSize) || this._inputs.coverage.rangeSize <= 0);
    }

    get minProfitMissing(): boolean {
        const amount = this._inputs && this._inputs.minProfit ? this._inputs.minProfit.amount : null;
        return !isFiniteNumber(amount) || (amount as number) <= 0;
    }

    get riskMissing(): boolean {
        const mr = this._inputs ? this._inputs.maxRisk : null;
        if (!mr) {
            return true;
        }
        if (mr.kind === 'dollar') {
            return !(isFiniteNumber(mr.amount) && (mr.amount as number) > 0);
        }
        return !mr.level;
    }

    // The direction sentence under the coverage buttons. It is worth spelling out because the
    // trader never enters a start and cannot see one: telling him where the coverage begins is
    // the only thing that makes the end strike he types meaningful. Leader ruling L69 keeps it —
    // removing it would not protect him from an engine decision, it would make his own control
    // unusable, the same category as telling him a field is in dollars.
    //
    // ⚠⚠ NEVER RENDER THE COMPUTED ANCHOR HERE. "Starts at 7750" is the natural,
    // helpful-looking improvement and it is the FORBIDDEN one (L69). Two reasons, either alone
    // sufficient:
    //   1. It surfaces an ENGINE DECISION before the run — the anchor reveals the zone rule's
    //      #2B-vs-#3 verdict, and under L67 the engine's decisions are not reported.
    //   2. IT CAN BE WRONG. L18 accepts the predicted anchor differing from the delivered one by
    //      a strike step. A number that leaks a verdict AND may not survive the run is worse than
    //      a sentence that explains a unit.
    //
    // THE TEST, so the next person does not have to ask: WOULD THE TEXT CHANGE FROM RUN TO RUN?
    // The same sentence for every trader on every run is a DEFINITION and belongs here. A per-run
    // figure is an OUTCOME and does not. `maven-anchor-hint.spec.ts` pins this by asserting the
    // sentence carries no digits at all, so an interpolated strike goes red rather than shipping.
    get anchorHint(): string {
        const from = this.side === 'calls' ? 'short call' : 'short put';
        const direction = this.side === 'calls' ? 'upward' : 'downward';
        return 'Starts at the chosen adjustment\'s ' + from + ' plus the offset, and covers ' + direction + '.';
    }

    // ---- Enabling the side ------------------------------------------------------------------

    @DetectMethodChanges()
    onEnabledChanged(e: any): void {
        if (!e || !e.event || !this._inputs) {
            return;   // programmatic echo, not a user action
        }
        this._inputs.enabled = !!e.value;
        // Checking opens, unchecking closes (owner, 2026-09-13).
        this.expanded = this._inputs.enabled;
        this.changed.emit();
    }

    // ---- Coverage (S8) ----------------------------------------------------------------------

    readonly coverageButtons: { id: MavenCoverageKind; text: string }[] = [
        {id: 'range', text: 'Range'},
        {id: 'followThrough', text: 'Follow-Through'},
        {id: 'expectedRange', text: 'Expected Range'}
    ];

    get coverageItems(): any[] {
        return this.coverageButtons.map(b => ({
            id: b.id,
            text: b.text,
            disabled: b.id === 'expectedRange' && !this.expectedRangeAvailable,
            hint: (b.id === 'expectedRange' && !this.expectedRangeAvailable)
                ? 'Your Expected Move setting cannot produce a range right now.'
                : undefined
        }));
    }

    get coverageKeys(): MavenCoverageKind[] {
        return this._inputs && this._inputs.coverage ? [this._inputs.coverage.kind] : [];
    }

    get isRangeCoverage(): boolean {
        return !!this._inputs && !!this._inputs.coverage && this._inputs.coverage.kind === 'range';
    }

    @DetectMethodChanges()
    onCoverageClick(e: any): void {
        if (!this._inputs || !e || !e.itemData || e.itemData.disabled) {
            return;
        }
        const kind = e.itemData.id as MavenCoverageKind;
        if (this._inputs.coverage.kind === kind) {
            return;
        }
        // Remember the range separately; active inputs still contain only the selected choice.
        this.rememberRangeCoverage();
        this._inputs.coverage = kind === 'range'
            ? this.restoreRangeCoverage()
            : {kind, rangeMode: 'endStrike', rangeSize: null, endStrike: null};
        this.changed.emit();
    }

    readonly rangeModeButtons = [
        {id: 'endStrike', text: 'End Strike'},
        {id: 'distance', text: 'Distance'}
    ];

    get rangeModeKeys(): MavenRangeMode[] {
        return [mavenRangeMode(this._inputs ? this._inputs.coverage : null)];
    }

    get isDistanceRange(): boolean {
        return !!this._inputs && mavenRangeMode(this._inputs.coverage) === 'distance';
    }

    @DetectMethodChanges()
    onRangeModeClick(e: any): void {
        if (this.disabled || !this.isRangeCoverage || !e || !e.itemData) { return; }
        const mode = e.itemData.id as MavenRangeMode;
        if ((mode !== 'endStrike' && mode !== 'distance') || mode === mavenRangeMode(this._inputs.coverage)) { return; }
        this.rememberRangeCoverage();
        this._inputs.coverage = this.restoreRangeCoverage(mode);
        this.changed.emit();
    }

    private rememberRangeCoverage(): void {
        const current = this._inputs.coverage;
        if (current.kind !== 'range') { return; }
        const mode = mavenRangeMode(current);
        const previous = this._parkedRangeCoverage;
        this._parkedRangeCoverage = {
            kind: 'range', rangeMode: mode,
            endStrike: mode === 'endStrike' ? current.endStrike : previous ? previous.endStrike : null,
            rangeSize: mode === 'distance' ? current.rangeSize : previous ? previous.rangeSize : null
        };
    }

    private restoreRangeCoverage(mode = mavenRangeMode(this._parkedRangeCoverage)): MavenCoverage {
        const remembered = this._parkedRangeCoverage;
        return {
            kind: 'range', rangeMode: mode,
            endStrike: mode === 'endStrike' && remembered ? remembered.endStrike : null,
            rangeSize: mode === 'distance' && remembered ? remembered.rangeSize : null
        };
    }

    get rangeSize(): number | null {
        const value = this._inputs && this._inputs.coverage ? this._inputs.coverage.rangeSize : null;
        return value == null ? null : value;
    }

    @DetectMethodChanges()
    onRangeSizeChanged(e: any): void {
        if (this.disabled || !this.isRangeCoverage || !this.isDistanceRange || !e || !e.event) { return; }
        this._inputs.coverage.rangeSize = isFiniteNumber(e.value) ? e.value : null;
        this.changed.emit();
    }

    get endStrike(): number | null {
        return this._inputs && this._inputs.coverage ? this._inputs.coverage.endStrike : null;
    }

    @DetectMethodChanges()
    onEndStrikeChanged(strike: number): void {
        if (!this._inputs || !this._inputs.coverage) {
            return;
        }
        this._inputs.coverage.endStrike = (typeof strike === 'number' && isFinite(strike)) ? strike : null;
        this.changed.emit();
    }

    // ---- Offset from shorts (S7) ------------------------------------------------------------

    get offset(): number | null {
        return this._inputs ? this._inputs.offsetFromShorts : null;
    }

    // The offset spins and snaps in multiples of the underlying's strike step (SPX 5, default 1)
    // — the ORION (OPG)'s own rule for its offset box, read from the same helper so the two
    // panels cannot disagree about what a step is (owner, vrequest 2026-09-13 round 3).
    get strikeStep(): number {
        return getStrikeStep(this.targetInstrument ? this.targetInstrument.ticker : null);
    }

    @DetectMethodChanges()
    onOffsetChanged(e: any): void {
        if (!e || !e.event || !this._inputs) {
            return;
        }
        const v = e.value;
        const raw = (typeof v === 'number' && isFinite(v)) ? v : null;
        // Typed values snap to the nearest step too, so 3 on SPX becomes 5 rather than an
        // un-listed strike offset. Zero and negatives are legitimate (an in-the-money offset).
        this._inputs.offsetFromShorts = snapToStrikeStep(raw, this.strikeStep, false);
        this.changed.emit();
    }

    // ---- Minimum profit (S6) ----------------------------------------------------------------

    readonly moneyModeButtons = [
        {id: 'normalized', text: 'Normalized'},
        {id: 'raw', text: 'Raw ($)'}
    ];

    get minProfitModeKeys(): string[] {
        return [this._inputs && this._inputs.minProfit && this._inputs.minProfit.normalized ? 'normalized' : 'raw'];
    }

    get minProfit(): number | null {
        return this._inputs && this._inputs.minProfit ? this._inputs.minProfit.amount : null;
    }

    @DetectMethodChanges()
    onMinProfitModeClick(e: any): void {
        if (!this._inputs || !e || !e.itemData) {
            return;
        }
        const toNormalized = e.itemData.id === 'normalized';
        if (this._inputs.minProfit.normalized === toNormalized) {
            return;
        }
        // Convert the number in place so it keeps SAYING THE SAME THING across the flip. The
        // shared helper is the one definition of that conversion; re-deriving it here would be a
        // second definition of the same money.
        this._inputs.minProfit.amount = convertOnNormalizedModeFlip(
            this._inputs.minProfit.amount, toNormalized, this.defaultQty);
        this._inputs.minProfit.normalized = toNormalized;
        this.changed.emit();
    }

    @DetectMethodChanges()
    onMinProfitChanged(e: any): void {
        if (!e || !e.event || !this._inputs) {
            return;
        }
        const v = e.value;
        this._inputs.minProfit.amount = (typeof v === 'number' && isFinite(v)) ? v : null;
        this.changed.emit();
    }

    // ---- Max risk (S9) ----------------------------------------------------------------------

    // ⚠ THESE LABELS ARE THE ORION (OPG)'s, VERBATIM AND IN ITS ORDER — not Minimum Profit's, and
    // the difference is deliberate. If you are here because Max Risk looks inconsistent with the
    // Minimum Profit selector directly above it, READ THIS BEFORE "FIXING" IT:
    //
    // Alan ruled that asymmetry in July 2026 (vrequest 2026-07-20), and the ruling is recorded in
    // the donor's own template (copilot-shared/opg-side-questionnaire.component.html:261-267):
    // a matching "Normalized (Def. Qty) | Raw ($)" pair here would put "Raw ($)" one row beneath
    // the "Dollar ($)" selector above — two unrelated meanings of $ stacked together ($ = a dollar
    // CEILING rather than a relative level, vs $ = position-total rather than per-unit). His words:
    // "Do NOT 'harmonize' this control with the one above."
    //
    // These buttons pick a KIND — a relative band or a dollar ceiling — not a money mode. The
    // earlier "Level" / "Dollar cap" wording was invented here and matched nothing; Q-M1 rules
    // that Maven's max risk IS the OPG's control, so it now reads as the OPG reads.
    readonly riskKindButtons = [
        {id: 'dollar', text: 'Dollar ($)'},
        {id: 'relative', text: 'Relative'}
    ];

    // Derived from the ORION (OPG)'s own constant, never re-typed: exactly
    // Low | Moderate | Aggressive | All. A fifth value would be a contract violation.
    readonly riskLevelButtons = OPG_RELATIVE_RISK_LEVELS.map(l => ({id: l, text: l}));

    get riskKindKeys(): string[] {
        return [this._inputs && this._inputs.maxRisk ? this._inputs.maxRisk.kind : 'relative'];
    }

    get isRelativeRisk(): boolean {
        return !!this._inputs && !!this._inputs.maxRisk && this._inputs.maxRisk.kind === 'relative';
    }

    get riskLevelKeys(): string[] {
        const mr = this._inputs ? this._inputs.maxRisk : null;
        return (mr && mr.kind === 'relative' && mr.level) ? [mr.level] : [];
    }

    get riskAmount(): number | null {
        const mr = this._inputs ? this._inputs.maxRisk : null;
        return (mr && mr.kind === 'dollar') ? mr.amount : null;
    }

    get riskNormalized(): boolean {
        const mr = this._inputs ? this._inputs.maxRisk : null;
        return !!(mr && mr.kind === 'dollar' && mr.normalized);
    }

    get riskModeKeys(): string[] {
        return [this.riskNormalized ? 'normalized' : 'raw'];
    }

    @DetectMethodChanges()
    onRiskKindClick(e: any): void {
        if (!this._inputs || !e || !e.itemData) {
            return;
        }
        const kind = e.itemData.id;
        if (this._inputs.maxRisk.kind === kind) {
            return;
        }
        // Keep each mode's choice without inventing a conversion between a level and dollars.
        const current = this._inputs.maxRisk;
        if (current.kind === 'dollar') {
            this._parkedDollarRisk = {...current};
        } else {
            this._parkedRelativeRisk = {...current};
        }
        this._inputs.maxRisk = kind === 'dollar'
            ? {...(this._parkedDollarRisk || {kind: 'dollar', amount: null, normalized: true})}
            : {...(this._parkedRelativeRisk || {kind: 'relative', level: null})};
        this.changed.emit();
    }

    @DetectMethodChanges()
    onRiskLevelClick(e: any): void {
        if (!this._inputs || !e || !e.itemData || this._inputs.maxRisk.kind !== 'relative') {
            return;
        }
        this._inputs.maxRisk = {kind: 'relative', level: e.itemData.id as MavenRiskLevel};
        this.changed.emit();
    }

    @DetectMethodChanges()
    onRiskAmountChanged(e: any): void {
        if (!e || !e.event || !this._inputs || this._inputs.maxRisk.kind !== 'dollar') {
            return;
        }
        const v = e.value;
        this._inputs.maxRisk.amount = (typeof v === 'number' && isFinite(v)) ? v : null;
        this.changed.emit();
    }

    @DetectMethodChanges()
    onRiskModeClick(e: any): void {
        if (!this._inputs || !e || !e.itemData || this._inputs.maxRisk.kind !== 'dollar') {
            return;
        }
        const toNormalized = e.itemData.id === 'normalized';
        if (this._inputs.maxRisk.normalized === toNormalized) {
            return;
        }
        this._inputs.maxRisk.amount = convertOnNormalizedModeFlip(
            this._inputs.maxRisk.amount, toNormalized, this.defaultQty);
        this._inputs.maxRisk.normalized = toNormalized;
        this.changed.emit();
    }
}

function isFiniteNumber(v: any): boolean {
    return typeof v === 'number' && isFinite(v);
}
